import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useAuth } from '../App';

const API = process.env.REACT_APP_API_URL || '/api';

function PronoModal({ match, onClose, onSaved }) {
  const [home, setHome] = useState('');
  const [away, setAway] = useState('');
  const [loading, setLoading] = useState(false);
  const { showToast } = useAuth();

  const submit = async () => {
    const h = parseInt(home), a = parseInt(away);
    if (isNaN(h) || isNaN(a) || h < 0 || a < 0) {
      showToast('Scores invalides', 'error'); return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/pronos`, { match_id: match.id, home_score: h, away_score: a });
      showToast(`✅ Prono ${h}-${a} enregistré !`);
      onSaved();
      onClose();
    } catch (e) {
      showToast(e.response?.data?.detail || 'Erreur', 'error');
    } finally { setLoading(false); }
  };

  return (
    <div style={{
      position:'fixed',inset:0,background:'rgba(0,0,0,.7)',
      display:'flex',alignItems:'center',justifyContent:'center',
      zIndex:200,padding:20,backdropFilter:'blur(6px)'
    }} onClick={onClose}>
      <div className="prono-form" style={{width:'100%',maxWidth:440}} onClick={e=>e.stopPropagation()}>
        <div style={{marginBottom:20}}>
          <div className="match-stage">{match.stage}</div>
          <h2 style={{fontFamily:'var(--font-head)',fontSize:'1.6rem',letterSpacing:2,marginTop:4}}>
            VOTRE PRONO
          </h2>
        </div>
        <div className="prono-teams">
          <div className="match-team" style={{alignItems:'center'}}>
            <span className="team-flag">{match.home.flag}</span>
            <span className="team-name">{match.home.name}</span>
            <input
              className="score-input"
              type="number" min="0" max="20"
              value={home} onChange={e=>setHome(e.target.value)}
              placeholder="0"
              autoFocus
            />
          </div>
          <span className="score-sep">—</span>
          <div className="match-team" style={{alignItems:'center'}}>
            <span className="team-flag">{match.away.flag}</span>
            <span className="team-name">{match.away.name}</span>
            <input
              className="score-input"
              type="number" min="0" max="20"
              value={away} onChange={e=>setAway(e.target.value)}
              placeholder="0"
            />
          </div>
        </div>
        <div style={{display:'flex',gap:10}}>
          <button className="btn btn-ghost" onClick={onClose} style={{flex:1}}>Annuler</button>
          <button className="btn btn-primary" onClick={submit} disabled={loading} style={{flex:2}}>
            {loading ? '...' : '🎯 Valider le prono'}
          </button>
        </div>
        <p style={{marginTop:16,fontSize:12,color:'var(--muted)',textAlign:'center'}}>
          Vous pouvez modifier votre prono jusqu'au coup d'envoi.
        </p>
      </div>
    </div>
  );
}

// Drapeaux emoji unicode par code équipe
const FLAGS = {
  FRA:'🇫🇷',ESP:'🇪🇸',ENG:'🏴󠁧󠁢󠁥󠁮󠁧󠁿',GER:'🇩🇪',POR:'🇵🇹',NED:'🇳🇱',BEL:'🇧🇪',
  CRO:'🇭🇷',AUT:'🇦🇹',SUI:'🇨🇭',SCO:'🏴󠁧󠁢󠁳󠁣󠁴󠁿',TUR:'🇹🇷',NOR:'🇳🇴',SWE:'🇸🇪',
  CZE:'🇨🇿',BIH:'🇧🇦',ARG:'🇦🇷',BRA:'🇧🇷',USA:'🇺🇸',MEX:'🇲🇽',CAN:'🇨🇦',
  URU:'🇺🇾',COL:'🇨🇴',ECU:'🇪🇨',PAR:'🇵🇾',PAN:'🇵🇦',HAI:'🇭🇹',CUW:'🇨🇼',
  MAR:'🇲🇦',SEN:'🇸🇳',EGY:'🇪🇬',NGA:'🇳🇬',CIV:'🇨🇮',GHA:'🇬🇭',RSA:'🇿🇦',
  TUN:'🇹🇳',ALG:'🇩🇿',CMR:'🇨🇲',COD:'🇨🇩',JPN:'🇯🇵',KOR:'🇰🇷',IRN:'🇮🇷',
  SAU:'🇸🇦',AUS:'🇦🇺',QAT:'🇶🇦',UZB:'🇺🇿',NZL:'🇳🇿',IRQ:'🇮🇶',JOR:'🇯🇴',CPV:'🇨🇻',
};

function Flag({ code, size = 40 }) {
  return (
    <span style={{fontSize: size, lineHeight: 1, display:'block'}}>
      {FLAGS[code] || '🏳'}
    </span>
  );
}

function MatchCard({ match, onProno }) {
  const { user } = useAuth();
  const kickoff = match.kickoff ? parseISO(match.kickoff) : null;
  const dateStr = kickoff ? format(kickoff, "EEE d MMM · HH'h'mm", { locale: fr }) : '';
  const isFinished = match.status === 'FINISHED';
  const isLive = match.status === 'IN_PLAY';

  return (
    <div className="card" style={{marginBottom:10}}>
      <div className="match-card">
        {/* Équipe domicile */}
        <div className="match-team">
          <Flag code={match.home.code} size={44} />
          <span className="team-name">{match.home.name}</span>
        </div>

        {/* Centre */}
        <div className="match-center">
          {isFinished && match.score ? (
            <div className="match-score">{match.score.home} — {match.score.away}</div>
          ) : isLive ? (
            <div className="match-score" style={{color:'var(--danger)'}}>🔴 EN DIRECT</div>
          ) : (
            <div className="match-vs">VS</div>
          )}
          <div className="match-time">{dateStr} UTC</div>
          {isFinished && <span className="badge badge-done">Terminé</span>}
          {isLive && <span className="badge badge-live">Live</span>}
          {!isFinished && !isLive && <span className="badge badge-soon">{match.stage}</span>}
        </div>

        {/* Équipe extérieure */}
        <div className="match-team">
          <Flag code={match.away.code} size={44} />
          <span className="team-name">{match.away.name}</span>
        </div>
      </div>

      {!isFinished && (
        <div className="match-card-footer">
          <span style={{fontSize:12,color:'var(--muted)'}}>⚽ {match.stage}</span>
          {user
            ? <button className="btn btn-primary" style={{padding:'7px 16px',fontSize:13,width:'auto'}}
                onClick={() => onProno(match)}>
                🎯 Mon prono
              </button>
            : <button className="btn btn-ghost" style={{padding:'7px 16px',fontSize:13}}
                onClick={() => window.location.href='/login'}>
                Connexion pour pronostiquer
              </button>
          }
        </div>
      )}
    </div>
  );
}

export default function Matches() {
  const [matches, setMatches] = useState([]);
  const [tab, setTab] = useState('upcoming');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/matches?status=${tab}&limit=30`);
      setMatches(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [tab]);

  return (
    <div className="container" style={{padding:'32px 20px'}}>
      {/* Hero */}
      <div className="hero" style={{borderRadius:'var(--radius)',marginBottom:32}}>
        <h1 className="hero-title">CDM <span className="hl">2026</span></h1>
        <p className="hero-sub">Pronostique les matchs, gagne des points, défie tes amis.</p>
        <div className="hero-badge">
          <span className="dot" />
          104 matchs · 48 équipes · 11 juin – 19 juillet 2026
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        {[['upcoming','📅 À venir'],['finished','✅ Terminés'],['all','🌍 Tous']].map(([v,l]) => (
          <button key={v} className={`tab ${tab===v?'active':''}`} onClick={() => setTab(v)}>{l}</button>
        ))}
      </div>

      {loading ? <div className="spinner" /> : (
        matches.length === 0
          ? <div className="empty">Aucun match</div>
          : matches.map(m => (
              <MatchCard key={m.id} match={m} onProno={setSelected} />
            ))
      )}

      {selected && (
        <PronoModal match={selected} onClose={() => setSelected(null)} onSaved={load} />
      )}
    </div>
  );
}
